"use client"

import { datadogRum } from "@datadog/browser-rum"
import { ReferralData, useSignup } from "@nelo/signup-lib"
import debounce from "lodash/debounce"
import React, { useCallback, useEffect, useMemo, useState } from "react"

import { sendGoogleAnalyticsEvent } from "@modules/common/utils/analytics"
import { CheckoutExperimentsDefinition } from "@modules/signup-and-order-flow/experiments/experiment-interfaces"

import AddressStep from "./steps/AddressStep"
import CurpEntryStep from "./steps/CurpEntryStep"
import OccupationIncomeStep from "./steps/OccupationIncomeStep"
import PersonalInfoStep from "./steps/PersonalInfoStep"
import PhoneEmailStep from "./steps/PhoneEmailStep"
import PinSetupStep from "./steps/PinSetupStep"
import VerificationCodeStep from "./steps/VerificationCodeStep"

enum StepId {
  PHONE_EMAIL = "PHONE_EMAIL",
  VERIFICATION_CODE = "VERIFICATION_CODE",
  PERSONAL_INFO = "PERSONAL_INFO",
  VERIFY_CURP = "VERIFY_CURP",
  INCOME_VALIDATION = "INCOME_VALIDATION",
  ADDRESS_INFO = "ADDRESS_INFO",
  CREATE_PIN = "CREATE_PIN",
}

export interface ErrorHandler {
  isLoading: boolean
  error: string | null
}

export interface StepComponentProps {
  stepId: string
  errorHandler: ErrorHandler
  onComplete: () => void
  onBack: () => void
  onClose: () => void
  progressPercentage: number
  treatments: CheckoutExperimentsDefinition
}

export interface SignupFlowProps {
  onSignupSuccess: (token: string, userUuid: string) => void
  onClose: () => void
  flowType: string
  treatments: CheckoutExperimentsDefinition
  initialReferralData: ReferralData
}

const stepRegistry: Record<string, React.ComponentType<StepComponentProps>> = {
  [StepId.PHONE_EMAIL]: PhoneEmailStep,
  [StepId.VERIFICATION_CODE]: VerificationCodeStep,
  [StepId.PERSONAL_INFO]: PersonalInfoStep,
  [StepId.VERIFY_CURP]: CurpEntryStep,
  [StepId.INCOME_VALIDATION]: OccupationIncomeStep,
  [StepId.ADDRESS_INFO]: AddressStep,
  [StepId.CREATE_PIN]: PinSetupStep,
}

const SignupFlowSteps: React.FC<SignupFlowProps> = ({ onSignupSuccess, onClose, flowType, treatments, initialReferralData }) => {
  const { referralData } = useSignup()

  useEffect(() => {
    referralData.setReferralData(initialReferralData)
  }, [initialReferralData, referralData])

  const stepOrder = [
    StepId.PHONE_EMAIL,
    StepId.VERIFICATION_CODE,
    StepId.PERSONAL_INFO,
    StepId.VERIFY_CURP,
    StepId.INCOME_VALIDATION,
    StepId.ADDRESS_INFO,
  ]

  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)
  const [hasTrackedBeginCheckout, setHasTrackedBeginCheckout] = useState(false)

  const { submitSignup } = useSignup()

  const goToNextStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.min(prev + 1, stepOrder.length - 1))
  }, [stepOrder.length])

  const goToPreviousStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1))
  }, [])

  const handleSignupSuccess = useCallback(
    (token: string, userUuid: string) => {
      onSignupSuccess(token, userUuid)
    },
    [onSignupSuccess]
  )

  useEffect(() => {
    if (!hasTrackedBeginCheckout) {
      sendGoogleAnalyticsEvent("SIGNUP_FLOW_START", { flow_type: flowType })
      datadogRum.addAction(`signup.start`)
      setHasTrackedBeginCheckout(true)
    }
  }, [flowType, hasTrackedBeginCheckout])

  useEffect(() => {
    if (currentStepIndex >= 0 && currentStepIndex < stepOrder.length) {
      const currentStepId = stepOrder[currentStepIndex]
      sendGoogleAnalyticsEvent("SIGNUP_FLOW_PAGE_VIEW", {
        flow_type: flowType,
        step: currentStepId,
      })
      datadogRum.addAction(`signup.${currentStepId}`)
    }
  }, [currentStepIndex, stepOrder, flowType])

  useEffect(() => {
    setIsInitialized(true)
  }, [])

  const calculateProgressPercentage = useCallback((): number => {
    const totalSteps = Math.max(1, stepOrder.length - 1)
    const progressPerStep = (100 - 15) / totalSteps
    return Math.round(15 + currentStepIndex * progressPerStep)
  }, [currentStepIndex, stepOrder.length])

  const errorHandler: ErrorHandler = useMemo(
    () => ({
      isLoading: submitSignup.isLoading || false,
      error: submitSignup.error?.message || null,
    }),
    [submitSignup]
  )

  const doSignup = useMemo(
    () =>
      debounce(
        async () => {
          try {
            const result = await submitSignup.execute()
            if (result && result.token) {
              handleSignupSuccess(result.token, result.userUuid)
              return true
            }
            console.error("No token received from submitSignup.execute()")
            datadogRum.addError("No token received from submitSignup.execute()", {
              step: stepOrder[currentStepIndex],
            })
            return false
          } catch (error) {
            console.error("Error submitting signup:", error)
            datadogRum.addError(error, {
              step: stepOrder[currentStepIndex],
            })
            return false
          }
        },
        200,
        { leading: true, trailing: false }
      ),
    [submitSignup, handleSignupSuccess, stepOrder, currentStepIndex]
  )

  const handleStepComplete = useCallback(async () => {
    if (currentStepIndex === stepOrder.length - 1) {
      const success = await doSignup()
      if (success) {
        return
      }
    }
    goToNextStep()
  }, [currentStepIndex, stepOrder.length, goToNextStep, doSignup])

  const currentStepId = stepOrder[currentStepIndex]
  const StepComponent = stepRegistry[currentStepId]

  const stepProps: StepComponentProps = useMemo(
    () => ({
      stepId: currentStepId,
      progressPercentage: calculateProgressPercentage(),
      onComplete: handleStepComplete,
      onBack: currentStepIndex > 0 ? goToPreviousStep : onClose,
      onClose,
      errorHandler,
      treatments,
    }),
    [currentStepId, calculateProgressPercentage, handleStepComplete, currentStepIndex, goToPreviousStep, onClose, errorHandler, treatments]
  )

  if (!isInitialized) {
    return <div className="flex items-center justify-center p-8">Cargando...</div>
  }

  return (
    <div className="flex flex-col">
      <StepComponent {...stepProps} />
    </div>
  )
}

export default SignupFlowSteps
